/**
 * Privacy Policy — static public page, no auth required.
 * Must satisfy Google API Services User Data Policy + Limited Use requirements.
 */
import { Zap } from "lucide-react";
import { Link } from "react-router-dom";

const EFFECTIVE_DATE = "July 14, 2026";

export function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-surface-secondary">
      <div className="max-w-3xl mx-auto px-4 py-12">
        {/* Header */}
        <Link to="/" className="flex items-center gap-2 mb-10">
          <Zap size={24} className="text-brand" />
          <span className="text-[20px] font-bold text-brand" style={{ letterSpacing: "-0.02em" }}>enereg</span>
        </Link>

        <article className="bg-surface border border-border rounded-2xl p-6 sm:p-10">
          <h1 className="text-[28px] font-bold text-text-primary mb-1" style={{ letterSpacing: "-0.02em" }}>Privacy Policy</h1>
          <p className="text-[14px] text-text-tertiary mb-8">Effective date: {EFFECTIVE_DATE}</p>

          <div className="space-y-8 text-[15px] leading-relaxed text-text-secondary">
            {/* 1 */}
            <Section title="1. Introduction">
              <p>
                Enereg ("we", "our", "us") operates the Enereg platform at app.enereg.com — an AI-powered regulatory
                licensing and permitting platform for renewable energy project developers in Africa. This Privacy Policy
                explains how we collect, use, store, and protect your information when you use our platform.
              </p>
              <p>
                By creating an account or using any Enereg service, you agree to this Privacy Policy. If you do not agree,
                please do not use the platform.
              </p>
            </Section>

            {/* 2 */}
            <Section title="2. Information We Collect">
              <h4 className="font-semibold text-text-primary mt-4 mb-2">Account Information</h4>
              <p>
                When you sign up, we collect your email address, name, and profile information provided by your
                authentication provider (Google or Microsoft), or your email address when you sign in with a magic link.
              </p>

              <h4 className="font-semibold text-text-primary mt-4 mb-2">Waitlist &amp; Regulatory Checkup</h4>
              <p>
                If you join our waitlist (on the marketing site or in the app), we collect your name, work email, company
                or project name, country, and project capacity range so we can contact you and set up your account.
              </p>
              <p className="mt-3">
                If you use the free <strong>Regulatory Checkup</strong> (no account required), we collect the project
                profile you enter — country, capacity, your intent, and answers about land tenure, grid status, and
                offtake. To show the full document checklist and to let you save or print the report, we ask for your
                email. Your email is stored with your checkup result and recorded as a lead; we use it only to send or
                save your report and to follow up with you about Enereg. We do not send marketing spam.
              </p>

              <h4 className="font-semibold text-text-primary mt-4 mb-2">Project Data</h4>
              <p>
                You provide project information including company details, site locations, financial figures, license
                application data, and regulatory field values. This data is stored in structured fields that you create,
                edit, and control.
              </p>

              <h4 className="font-semibold text-text-primary mt-4 mb-2">Documents</h4>
              <p>
                You may upload documents (PDFs, images, spreadsheets) directly or via cloud drive integration.
                We process these documents to extract structured data fields. See Sections 3 and 4 for details
                on cloud drive access and AI processing.
              </p>

              <h4 className="font-semibold text-text-primary mt-4 mb-2">Technical Data</h4>
              <p>
                We collect standard technical data including IP addresses (used for rate limiting only, not persisted
                to disk), browser type, and session identifiers. We use session cookies (7-day expiry) for authentication.
                We do not use tracking cookies, analytics services, or advertising pixels.
              </p>
            </Section>

            {/* 3 */}
            <Section title="3. Google Drive Integration">
              <p>
                Enereg offers optional integration with Google Drive to automatically sync project documents. When you
                choose to connect Google Drive:
              </p>
              <ul className="list-disc pl-5 space-y-2 mt-3">
                <li>
                  <strong>Scope of access:</strong> We request read-only access to your Google Drive
                  (<code className="text-[13px] bg-surface-secondary px-1 py-0.5 rounded">drive.readonly</code>).
                  We access only the specific folder you select through our folder picker — we never browse, search,
                  or access other files or folders in your Drive.
                </li>
                <li>
                  <strong>What we read:</strong> File metadata (name, size, type, last modified date) and file content
                  from your selected folder and its subfolders.
                </li>
                <li>
                  <strong>How we use it:</strong> Files are downloaded temporarily, classified by document type, and
                  processed by AI to extract structured regulatory data fields (see Section 5). The extracted field
                  values are stored in your project. The raw file content is processed in memory and is not permanently
                  stored on our servers.
                </li>
                <li>
                  <strong>What we store:</strong> OAuth tokens (encrypted), the folder ID you selected, file metadata
                  (name, ID, modification date), and extracted field values with confidence scores. We do not store
                  copies of your files.
                </li>
                <li>
                  <strong>We never write to, delete from, or modify your Google Drive</strong> in any way.
                </li>
                <li>
                  <strong>Disconnecting:</strong> You can disconnect Google Drive at any time from the Documents tab
                  in your project dashboard. Upon disconnection, we delete the stored OAuth tokens. Previously extracted
                  field values remain in your project unless you delete them.
                </li>
              </ul>
            </Section>

            {/* 4 */}
            <Section title="4. Microsoft OneDrive Integration">
              <p>
                Enereg also offers optional integration with Microsoft OneDrive. The same principles apply as described
                in Section 3 for Google Drive:
              </p>
              <ul className="list-disc pl-5 space-y-2 mt-3">
                <li>We request read-only access (<code className="text-[13px] bg-surface-secondary px-1 py-0.5 rounded">Files.Read.All</code>) to the specific folder you select.</li>
                <li>Files are processed for data extraction only — raw content is not permanently stored.</li>
                <li>We never write to, delete from, or modify your OneDrive.</li>
                <li>You can disconnect OneDrive at any time from your project dashboard.</li>
              </ul>
            </Section>

            {/* 5 */}
            <Section title="5. How We Use AI to Process Documents">
              <p>
                When you upload documents or sync them from a cloud drive, we use AI services to extract structured
                data from those documents:
              </p>
              <ul className="list-disc pl-5 space-y-2 mt-3">
                <li>
                  <strong>Document extraction:</strong> Document content is sent to the Anthropic Claude API to identify
                  and extract regulatory field values (e.g., company registration numbers, license dates, site coordinates).
                  Each extracted value includes a confidence score. High-confidence values are auto-committed; lower-confidence
                  values are flagged for your review.
                </li>
                <li>
                  <strong>Knowledge base embeddings:</strong> Regulatory reference documents (publicly available laws and
                  regulations) are processed into vector embeddings using the OpenAI API and stored in our knowledge base
                  for semantic search. Your private project data is not added to this knowledge base.
                </li>
                <li>
                  <strong>Evaluation enhancement:</strong> When generating project readiness assessments, we may send
                  anonymized project field values to the Anthropic Claude API to generate contextual guidance. This
                  guidance is displayed to you in the platform and is not stored externally.
                </li>
                <li>
                  <strong>Narrative generation:</strong> When you generate a submission template, the narrative
                  sections are drafted by a workflow service (Dify) that we run on our own infrastructure; the
                  underlying AI call uses the providers in Section 7. Only the field values needed for that document
                  are used.
                </li>
              </ul>
              <p className="mt-3">
                <strong>Your documents are not used to train AI models.</strong> Neither Anthropic nor OpenAI use data
                sent via their APIs to train their models. We do not use your data to develop, improve, or train any
                generalized AI or machine learning models.
              </p>
            </Section>

            {/* 6 */}
            <Section title="6. How We Use Your Information">
              <p>We use the information we collect to:</p>
              <ul className="list-disc pl-5 space-y-1 mt-3">
                <li>Provide the Enereg platform — project tracking, field management, document processing, license monitoring</li>
                <li>Extract structured data from your documents using AI</li>
                <li>Generate regulatory readiness assessments, gap analyses, and actionable guidance</li>
                <li>Generate pre-filled regulatory submission templates</li>
                <li>Send transactional sign-in emails (magic links) via Supabase. We do not run automated marketing email campaigns; any follow-up about your waitlist or checkup request is sent manually by a person.</li>
                <li>Maintain platform security through rate limiting and session management</li>
              </ul>
              <p className="mt-3">
                We do not sell your data. We do not use your data for advertising. We do not share your data with
                third parties except as described in Section 7.
              </p>
            </Section>

            {/* 7 */}
            <Section title="7. Third-Party Services">
              <p>We use the following third-party services to operate the platform:</p>
              <ul className="list-disc pl-5 space-y-2 mt-3">
                <li><strong>Anthropic (Claude API)</strong> — Document extraction and evaluation enhancement. Data is processed under Anthropic's commercial API terms and is not used for model training.</li>
                <li><strong>OpenAI (Embeddings API)</strong> — Vector embeddings for the regulatory knowledge base. Only public regulatory documents are processed, not your private project data.</li>
                <li><strong>Supabase</strong> — Authentication (OAuth login, magic-link sign-in emails, session management), the waitlist/lead table, and the vector database for the knowledge base. Hosted in the cloud with encryption at rest.</li>
                <li><strong>Fly.io</strong> — Application hosting. Our server and database run in the Johannesburg, South Africa region.</li>
                <li><strong>Google Cloud Platform</strong> — OAuth authentication and Google Drive API access (when you choose to connect).</li>
                <li><strong>Microsoft Azure</strong> — OAuth authentication and OneDrive API access (when you choose to connect).</li>
              </ul>
              <p className="mt-3">
                Each third-party service processes only the minimum data required for its function. We do not share
                your complete project data with any third party. The current list of sub-processors is maintained at{" "}
                <Link to="/sub-processors" className="text-brand hover:underline">enereg.com/sub-processors</Link>.
              </p>
            </Section>

            {/* 8 */}
            <Section title="8. Data Storage and Security">
              <ul className="list-disc pl-5 space-y-2">
                <li>Your project data is stored in an encrypted database on Fly.io infrastructure in the <strong>Johannesburg, South Africa</strong> region.</li>
                <li>All connections use HTTPS (TLS 1.3). HTTP requests are automatically redirected to HTTPS.</li>
                <li>Authentication uses secure, HTTP-only session cookies with 7-day expiry.</li>
                <li>We implement Content Security Policy (CSP), XSS protection, and other security headers.</li>
                <li>API endpoints are protected by rate limiting.</li>
                <li>OAuth tokens for cloud drive integrations are stored in the database alongside your project data and are only accessible to authenticated users within the same organization.</li>
                <li>We do not store passwords — authentication is handled via OAuth providers (Google, Microsoft) or magic link emails through Supabase.</li>
              </ul>
            </Section>

            {/* 9 */}
            <Section title="9. Data Retention and Deletion">
              <ul className="list-disc pl-5 space-y-2">
                <li><strong>Project data:</strong> Retained for as long as your account is active. When you delete a project, all associated field values, documents, and cloud drive connections are permanently deleted.</li>
                <li><strong>Account data:</strong> Retained for as long as your account is active. You can delete your account yourself at any time from your account settings — this permanently erases your account, projects, documents, feedback, and any waitlist/checkup records tied to your email. You may also ask us to do it by contacting us.</li>
                <li><strong>Waitlist &amp; checkup leads:</strong> Email and the details you submitted are retained for up to <strong>24 months</strong> from your last contact with us, then deleted, unless you become an account holder or ask us to remove them sooner. You can be removed at any time by emailing us (see Section 14).</li>
                <li><strong>Inactive accounts:</strong> If an account has not been used for <strong>24 months</strong>, we may notify you and then delete it and its data.</li>
                <li><strong>Cloud drive tokens:</strong> Retained until you disconnect the drive or delete the project. Tokens are refreshed automatically and the previous token is overwritten.</li>
                <li><strong>Session data:</strong> Session cookies expire after 7 days of inactivity.</li>
                <li><strong>Document content:</strong> Raw file content from uploaded or synced documents is processed in memory for extraction and is not retained after processing. Only extracted field values and file metadata are stored.</li>
              </ul>
            </Section>

            {/* 10 */}
            <Section title="10. Your Rights">
              <p>You have the right to:</p>
              <ul className="list-disc pl-5 space-y-1 mt-3">
                <li><strong>Access</strong> your data — all project data is visible in your dashboard at all times.</li>
                <li><strong>Correct</strong> your data — you can edit any field value directly in the platform.</li>
                <li><strong>Delete</strong> your data — you can delete individual field values, documents, or entire projects, and delete your whole account yourself from your account settings.</li>
                <li><strong>Disconnect</strong> cloud drives — you can remove Google Drive or OneDrive access at any time from the Documents tab.</li>
                <li><strong>Export</strong> your data (portability) — you can download a machine-readable copy of all your personal data (account, projects and field values, document metadata, feedback, and checkup records) from your account settings.</li>
                <li><strong>Opt out</strong> — if you are on our waitlist or submitted a checkup, you can ask us to delete your details at any time by emailing us; we do not run marketing email campaigns.</li>
                <li><strong>Withdraw consent</strong> — you can stop using the platform at any time.</li>
              </ul>
              <p className="mt-3">
                Most of these are self-serve in your account settings. To exercise any right that is not, or to have
                waitlist/checkup details removed, contact us at the address in Section 14. We aim to respond within 30 days.
              </p>
            </Section>

            {/* 11 */}
            <Section title="11. African Data Protection Compliance">
              <p>
                Enereg serves renewable energy developers in Africa. We are committed to complying with applicable
                data protection laws in the jurisdictions where our users operate:
              </p>
              <ul className="list-disc pl-5 space-y-2 mt-3">
                <li>
                  <strong>South Africa — POPIA (Protection of Personal Information Act):</strong> We process personal
                  information lawfully, for the specific purpose of providing our regulatory platform services.
                  We take reasonable measures to ensure data quality and security. You may lodge a complaint with
                  the Information Regulator.
                </li>
                <li>
                  <strong>Kenya — Data Protection Act, 2019:</strong> We collect and process personal data in accordance
                  with the principles of lawfulness, purpose limitation, and data minimization. You may lodge a complaint
                  with the Office of the Data Protection Commissioner.
                </li>
                <li>
                  <strong>Nigeria — NDPA (Nigeria Data Protection Act, 2023) / NDPR:</strong> We obtain consent before processing
                  personal data and implement appropriate security measures. You may lodge a complaint with the
                  Nigeria Data Protection Commission.
                </li>
              </ul>
              <p className="mt-4">
                <strong>Alignment with the EU GDPR.</strong> Although Enereg serves African markets and is not
                established in the EU, we have built our data practices to the standard of the EU General Data
                Protection Regulation as a widely-recognised baseline: a lawful basis for every processing activity
                (consent for waitlist/checkup and account creation; legitimate interest and contract for operating your
                projects), data minimisation, the data-subject rights in Section 10, encryption in transit and at rest,
                and data-processing terms with each processor in Section 7. We are not certified or formally "GDPR
                compliant", and the GDPR does not automatically govern users outside its scope, but we hold ourselves
                to its principles.
              </p>
            </Section>

            {/* 12 */}
            <Section title="12. International Data Transfers">
              <p>
                Your project data is stored in South Africa (Fly.io, Johannesburg region). When documents are processed,
                content is transmitted to:
              </p>
              <ul className="list-disc pl-5 space-y-1 mt-3">
                <li>Anthropic's API servers (United States) for document extraction and evaluation guidance</li>
                <li>OpenAI's API servers (United States) for embedding generation</li>
                <li>Supabase servers for authentication, magic-link emails, the waitlist table, and knowledge base storage</li>
              </ul>
              <p className="mt-3">
                Your waitlist and checkup details (email, name, company, country, capacity, and checkup answers) are
                stored in Supabase. These transfers are necessary to provide the service and are conducted under each
                provider's data processing agreements. Document content sent for AI processing is not retained by these
                providers and is not used for model training.
              </p>
            </Section>

            {/* 13 */}
            <Section title="13. Google API Services User Data Policy">
              <p>
                Enereg's use and transfer of information received from Google APIs adheres to the{" "}
                <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer"
                  className="text-brand hover:underline">
                  Google API Services User Data Policy
                </a>, including the Limited Use requirements.
              </p>
              <p className="mt-3">Specifically:</p>
              <ul className="list-disc pl-5 space-y-2 mt-3">
                <li>
                  We only use Google user data to provide and improve the user-facing features of Enereg
                  that are visible to you. We do not use Google data for any other purpose.
                </li>
                <li>
                  We do not transfer Google user data to third parties except: (a) as necessary to provide the
                  service (document extraction via Anthropic API), (b) with your explicit consent, (c) for
                  security purposes, or (d) to comply with applicable law.
                </li>
                <li>
                  We do not use Google user data for serving advertisements.
                </li>
                <li>
                  We do not use Google user data to develop, improve, or train generalized AI and/or
                  machine learning models.
                </li>
                <li>
                  Human access to Google user data is limited to: (a) when you have given explicit consent,
                  (b) when necessary for security purposes (investigating abuse or security incidents),
                  (c) to comply with applicable law, or (d) when the data is aggregated and anonymized
                  for internal operations.
                </li>
              </ul>
            </Section>

            {/* 14 */}
            <Section title="14. Contact Us">
              <p>
                If you have questions about this Privacy Policy, wish to exercise your data rights, or have concerns
                about how your data is handled, contact us at:
              </p>
              <p className="mt-3 font-medium text-text-primary">
                Enereg<br />
                Email: team+help@enereg.com
              </p>
              <p className="mt-4">
                We may update this Privacy Policy from time to time. We will notify you of significant changes by
                posting a notice on the platform. The "Effective date" at the top of this page indicates when this
                policy was last revised.
              </p>
            </Section>
          </div>
        </article>

        <footer className="mt-8 text-center text-[13px] text-text-tertiary">
          <Link to="/terms" className="hover:text-text-secondary">Terms of Service</Link>
          <span className="mx-2">&middot;</span>
          <Link to="/" className="hover:text-text-secondary">Back to enereg</Link>
        </footer>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="text-[18px] font-semibold text-text-primary mb-3">{title}</h2>
      {children}
    </section>
  );
}
